import React, { useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { SEO } from "@/components/common/SEO";
import { Users, Car, Newspaper, Rocket, Sparkles, LayoutDashboard, Building2, Inbox, Image as ImageIcon, Star, HelpCircle, Layers, GitCompareArrows, Settings } from "lucide-react";
import CarsAdmin from "@/pages/admin/CarsAdmin";
import BrandsAdmin from "@/pages/admin/BrandsAdmin";
import ArticlesAdmin from "@/pages/admin/ArticlesAdmin";
import MediaLibrary from "@/pages/admin/MediaLibrary";
import ReviewsAdmin from "@/pages/admin/ReviewsAdmin";
import FAQsAdmin from "@/pages/admin/FAQsAdmin";
import HeroSlidesAdmin from "@/pages/admin/HeroSlidesAdmin";
import ComparisonsAdmin from "@/pages/admin/ComparisonsAdmin";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, testId: "admin-nav-overview" },
  { to: "/admin/cars", label: "Cars", icon: Car, testId: "admin-nav-cars" },
  { to: "/admin/brands", label: "Brands", icon: Building2, testId: "admin-nav-brands" },
  { to: "/admin/articles", label: "Articles", icon: Newspaper, testId: "admin-nav-articles" },
  { to: "/admin/reviews", label: "Reviews", icon: Star, testId: "admin-nav-reviews" },
  { to: "/admin/comparisons", label: "Comparisons", icon: GitCompareArrows, testId: "admin-nav-comparisons" },
  { to: "/admin/hero", label: "Hero Slides", icon: Layers, testId: "admin-nav-hero" },
  { to: "/admin/media", label: "Media", icon: ImageIcon, testId: "admin-nav-media" },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle, testId: "admin-nav-faqs" },
  { to: "/admin/launches", label: "Launches", icon: Rocket, testId: "admin-nav-launches" },
  { to: "/admin/leads", label: "Leads", icon: Inbox, testId: "admin-nav-leads" },
  { to: "/admin/settings", label: "Site Settings", icon: Settings, testId: "admin-nav-settings" },
  { to: "/admin/users", label: "Users", icon: Users, testId: "admin-nav-users" },
  { to: "/admin/prompts", label: "AI Prompts", icon: Sparkles, testId: "admin-nav-prompts" },
];

function DataTable({title,rows,cols,testId}){return(<div data-testid={testId}><h1 className="font-display text-3xl tracking-tighter">{title}</h1><div className="mt-6 rounded-3xl border border-border overflow-hidden"><table className="w-full text-sm"><thead className="bg-secondary/40"><tr>{cols.map(c=><th key={c.key} className="text-left p-3 text-muted-foreground text-xs uppercase tracking-[0.15em]">{c.label}</th>)}</tr></thead><tbody>{rows.map((r,i)=>(<tr key={i} className="border-t border-border">{cols.map(c=><td key={c.key} className="p-3">{c.render?c.render(r):String(r[c.key]??"—")}</td>)}</tr>))}</tbody></table></div></div>);}

function Overview(){
  const [d,setD]=useState(null);
  useEffect(()=>{api.get("/admin/overview").then((r)=>setD(r.data));},[]);
  return(
    <div data-testid="admin-overview">
      <h1 className="font-display text-3xl tracking-tighter">Overview</h1>
      <p className="text-sm text-muted-foreground mt-1">A quick pulse of the content in your CMS.</p>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {d && Object.entries(d).map(([k,v])=>(
          <div key={k} className="rounded-3xl border border-border p-5 bg-white">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{k.replace(/_/g," ")}</div>
            <div className="font-display text-3xl mt-2">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LaunchesAdmin(){const [r,setR]=useState([]);useEffect(()=>{api.get("/launches").then((x)=>setR(x.data));},[]);return <DataTable testId="admin-launches" title="Launches" rows={r} cols={[{key:"model",label:"Model"},{key:"brand",label:"Brand"},{key:"expected_date",label:"Expected"},{key:"body_type",label:"Body"},{key:"fuel_type",label:"Fuel"}]}/>;}
function UsersAdmin(){const [r,setR]=useState([]);useEffect(()=>{api.get("/admin/users").then((x)=>setR(x.data));},[]);return <DataTable testId="admin-users" title="Users" rows={r} cols={[{key:"name",label:"Name"},{key:"email",label:"Email"},{key:"role",label:"Role"}]}/>;}
function PromptsAdmin(){const [r,setR]=useState([]);useEffect(()=>{api.get("/admin/prompts").then((x)=>setR(x.data));},[]);return(<div data-testid="admin-prompts"><h1 className="font-display text-3xl tracking-tighter">AI Prompts</h1><div className="mt-6 space-y-4">{r.map((p)=>(<div key={p.key} className="rounded-3xl border border-border p-5"><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{p.key} · {p.active?"active":"inactive"}</div><div className="font-display text-lg mt-1">{p.description}</div><textarea readOnly value={p.template} className="mt-3 w-full min-h-[80px] rounded-2xl bg-secondary/50 border border-border p-3 text-sm"/></div>))}</div></div>);}

const STATUS_STYLES = {
  new: "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  contacted: "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  converted: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  dropped: "bg-secondary text-muted-foreground",
};
const STATUSES = ["new", "contacted", "converted", "dropped"];

function LeadsAdmin() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [filter, setFilter] = useState("");
  const load = () => {
    const q = filter ? `?status=${filter}` : "";
    api.get(`/admin/leads${q}`).then((r) => setRows(r.data));
    api.get(`/admin/leads/stats`).then((r) => setStats(r.data));
  };
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    load();
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tighter">Leads</h1>
          <p className="text-sm text-muted-foreground mt-1">Buyer requests captured from Car pages — pipe these to your dealer network.</p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Total</div>
          <div className="font-display text-3xl">{stats.total || 0}</div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="lead-filter-row">
        <button data-testid="lead-filter-all" onClick={() => setFilter("")}
          className={`h-9 px-3 rounded-full border border-border text-xs ${filter === "" ? "bg-[#2563EB] text-white" : "bg-card"}`}>All ({stats.total || 0})</button>
        {STATUSES.map((s) => (
          <button key={s} data-testid={`lead-filter-${s}`} onClick={() => setFilter(s)}
            className={`h-9 px-3 rounded-full border border-border text-xs capitalize ${filter === s ? "bg-[#2563EB] text-white" : "bg-card"}`}>
            {s} ({stats.by_status?.[s] || 0})
          </button>
        ))}
      </div>

      <div className="mt-6 rounded-3xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr>
              <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">When</th>
              <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Buyer</th>
              <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Contact</th>
              <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Car</th>
              <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">City</th>
              <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground text-sm">No leads yet.</td></tr>
            )}
            {rows.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-border">
                <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                <td className="p-3 font-medium">{l.name}</td>
                <td className="p-3">
                  <a href={`tel:${l.phone}`} className="text-blue-600 hover:underline">{l.phone}</a>
                  {l.email && <div className="text-xs text-muted-foreground mt-0.5">{l.email}</div>}
                </td>
                <td className="p-3">
                  <div className="font-medium">{l.car_label}</div>
                  {l.variant && <div className="text-xs text-muted-foreground">Variant: {l.variant}</div>}
                </td>
                <td className="p-3">{l.city}</td>
                <td className="p-3">
                  <select data-testid={`lead-status-${l.id}`} value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)}
                    className={`text-xs h-8 px-2 rounded-full border border-border ${STATUS_STYLES[l.status] || ""}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminApp(){
  const {user,loading}=useAuth();
  if(loading) return <div className="p-12 text-muted-foreground">Loading…</div>;
  if(!user) return <Navigate to="/login" replace/>;
  if(user.role!=="admin") return <div className="max-w-md mx-auto p-12 text-center" data-testid="admin-forbidden"><div className="font-display text-2xl">Admins only</div><Link to="/" className="text-sm text-muted-foreground underline mt-3 inline-block">Go home</Link></div>;
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 grid lg:grid-cols-[220px,1fr] gap-8" data-testid="admin-shell">
      <SEO title="Admin · TrendingNewCar"/>
      <aside className="rounded-3xl border border-border bg-card/30 p-3 h-max sticky top-24">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground p-3">Admin</div>
        {NAV.map((n)=>(
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testId}
            className={({isActive})=>`flex items-center gap-3 px-3 py-2 rounded-xl text-sm ${isActive?"bg-secondary text-foreground":"text-muted-foreground hover:bg-secondary/60"}`}>
            <n.icon className="w-4 h-4"/> {n.label}
          </NavLink>
        ))}
      </aside>
      <div>
        <Routes>
          <Route index element={<Overview/>}/>
          <Route path="cars" element={<CarsAdmin/>}/>
          <Route path="brands" element={<BrandsAdmin/>}/>
          <Route path="articles" element={<ArticlesAdmin/>}/>
          <Route path="reviews" element={<ReviewsAdmin/>}/>
          <Route path="comparisons" element={<ComparisonsAdmin/>}/>
          <Route path="hero" element={<HeroSlidesAdmin/>}/>
          <Route path="media" element={<MediaLibrary/>}/>
          <Route path="faqs" element={<FAQsAdmin/>}/>
          <Route path="launches" element={<LaunchesAdmin/>}/>
          <Route path="leads" element={<LeadsAdmin/>}/>
          <Route path="settings" element={<SettingsAdmin/>}/>
          <Route path="users" element={<UsersAdmin/>}/>
          <Route path="prompts" element={<PromptsAdmin/>}/>
        </Routes>
      </div>
    </div>
  );
}
